import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { financeApi } from '@/services/finance.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Spinner, StatusBadge } from '@/components/ui';
import { CreateAccountDialog } from './create-account-dialog';

export function ChartOfAccountsPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);

  const query = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: () => financeApi.listAccounts(),
  });

  const accounts = query.data ?? [];

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-title">{t('finance.account.title')}</h1>
        {can('finance.account.create') && (
          <Button variant="primary" onClick={() => setIsCreating(true)}>
            {t('finance.account.create')}
          </Button>
        )}
      </header>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && accounts.length === 0 && (
        <EmptyState title={t('finance.account.empty')} />
      )}

      {accounts.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('finance.account.code')}</th>
                <th scope="col">{t('finance.account.name')}</th>
                <th scope="col">{t('finance.account.type')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id}>
                  <td dir="ltr">{account.code}</td>
                  <td>
                    {account.name}
                    {account.isSystem && (
                      <span className="cell-empty">
                        {' '}
                        · <StatusBadge status="system" label={t('finance.account.system')} />
                      </span>
                    )}
                  </td>
                  <td>{t(`finance.accountType.${account.type}`)}</td>
                  <td>
                    <Link to={`/finance/accounts/${account.id}/ledger`} className="nav-link">
                      {t('finance.account.viewLedger')}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isCreating && (
        <CreateAccountDialog
          onClose={() => setIsCreating(false)}
          onCreated={() => {
            setIsCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['finance', 'accounts'] });
          }}
        />
      )}
    </div>
  );
}
